'use client';

import { format } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Bot, BotMessage } from '@/types/bot';
import { MediaReferenceCard } from './MediaReferenceCard';
import { getBotAvatarSrc } from './getBotAvatarSrc';

interface BotChatMessageProps {
  message: BotMessage;
  bot: Bot;
  isUser: boolean;
  onMediaUnlock?: () => void;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatInlineMarkdown(value: string): string {
  return value
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
}

function renderMarkdownToHtml(content: string): string {
  const lines = content.split('\n');
  const blocks: string[] = [];
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    blocks.push(`<ol class="list-decimal pl-5 space-y-1">${listBuffer.join('')}</ol>`);
    listBuffer = [];
  };

  for (const rawLine of lines) {
    const escapedLine = formatInlineMarkdown(escapeHtml(rawLine.trim()));

    if (!rawLine.trim()) {
      flushList();
      continue;
    }

    const numberedMatch = escapedLine.match(/^\d+\.\s+(.*)$/);
    if (numberedMatch) {
      listBuffer.push(`<li>${numberedMatch[1]}</li>`);
      continue;
    }

    flushList();
    blocks.push(`<p>${escapedLine}</p>`);
  }

  flushList();
  return blocks.join('');
}

function formatMessageTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'h:mm a');
  } catch {
    return '';
  }
}

export function BotChatMessage({ message, bot, isUser, onMediaUnlock }: BotChatMessageProps) {
  const hasMediaReference = !isUser && message.media_reference != null;
  const avatarSrc = getBotAvatarSrc(bot);

  return (
    <div
      data-testid={`bot-message-${message.id}`}
      className={cn(
        'flex gap-3 px-4 py-2',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar - only show for bot messages */}
      <div className="w-10 shrink-0">
        {!isUser && (
          <Avatar className="jr-avatar-ring h-10 w-10">
            <AvatarImage src={avatarSrc} alt={bot.name} />
            <AvatarFallback className="bg-[color:var(--jr-surface-muted)] font-semibold text-[color:var(--jr-accent)]">
              {bot.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col max-w-[75%]',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        {/* Bot name and AI badge for bot messages */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-[var(--jr-text)]">{bot.name}</span>
            <Badge variant="secondary" className="jr-pill text-xs">
              AI
            </Badge>
          </div>
        )}

        {/* Message bubble */}
        <div
          className={cn(
            'break-words',
            isUser
              ? 'jr-chat-bubble-user'
              : 'jr-chat-bubble-bot'
          )}
        >
          <div
            className="text-sm leading-relaxed space-y-2 [&_code]:rounded [&_code]:bg-black/18 [&_code]:px-1 [&_code]:py-0.5 [&_ol]:my-2 [&_p]:whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(message.content) }}
          />
        </div>

        {/* Media reference card */}
        {hasMediaReference && message.media_reference && (
          <MediaReferenceCard
            mediaReference={message.media_reference}
            onUnlockSuccess={onMediaUnlock}
          />
        )}

        {/* Timestamp */}
        <span className="mt-1 px-1 text-xs text-[var(--jr-text-muted)]">
          {formatMessageTime(message.created_at)}
        </span>
      </div>
    </div>
  );
}
